import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { TID } from "@/constants/testIds";
import { Plus, X, Sparkles } from "lucide-react";
import { toast } from "sonner";

const initial = {
  title: "", description: "", requirements: "", preferred: "",
  salary_min: "", salary_max: "", pay_type: "hourly",
  benefits: "", schedule: "", shift: "",
  work_arrangement: "on-site", employment_type: "full-time",
  industry: "", city: "", state: "",
};

export default function EmployerJobs() {
  const [items, setItems] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(initial);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSkills, setAiSkills] = useState("");

  const load = async () => {
    const { data } = await api.get("/jobs/mine");
    setItems(data.items || []);
  };
  useEffect(() => { load(); api.get("/admin/industries").then(r => setIndustries(r.data.items || [])); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        salary_min: form.salary_min ? parseFloat(form.salary_min) : null,
        salary_max: form.salary_max ? parseFloat(form.salary_max) : null,
      };
      await api.post("/jobs", payload);
      toast.success("Job posted — pending agency approval");
      setCreating(false); setForm(initial);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post");
    }
  };

  const aiGenerate = async () => {
    if (!form.title) return toast.error("Enter a job title first");
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/generate-jd", {
        title: form.title, industry: form.industry,
        seniority: form.employment_type, key_skills: aiSkills.split(",").map(s => s.trim()).filter(Boolean),
      });
      setForm({ ...form, description: data.text });
      toast.success("Draft ready — edit before posting");
    } catch { toast.error("AI unavailable"); }
    finally { setAiBusy(false); }
  };

  const close = async (id) => {
    await api.put(`/jobs/${id}`, { is_active: false });
    toast.success("Closed");
    load();
  };

  return (
    <div>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow mb-2">Employer</div>
          <h1 className="font-display text-3xl font-bold text-navy">My Job Postings</h1>
        </div>
        <button onClick={() => setCreating(true)} className="btn-navy"><Plus className="w-4 h-4" /> Post a new job</button>
      </div>

      <div data-testid={TID.employerJobsTable} className="border border-slate-200 rounded-xl bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr className="text-left text-slate-500">
              <th className="p-3">Title</th><th className="p-3">Type</th><th className="p-3">Location</th>
              <th className="p-3">Status</th><th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(j => (
              <tr key={j.id} className="border-b last:border-0 hover:bg-slate-50">
                <td className="p-3 font-medium text-navy">{j.title}</td>
                <td className="p-3 text-slate-600">{j.employment_type} • {j.work_arrangement}</td>
                <td className="p-3 text-slate-600">{j.city}{j.state ? `, ${j.state}` : ""}</td>
                <td className="p-3 text-xs">
                  {!j.is_active ? <span className="text-slate-500">Closed</span>
                    : j.is_approved ? <span className="text-emerald-600">Live</span>
                    : <span className="text-amber-600">Pending review</span>}
                </td>
                <td className="p-3 text-xs">
                  {j.is_active && <button onClick={() => close(j.id)} className="text-red-500">Close</button>}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-500">No jobs yet — post your first opening.</td></tr>}
          </tbody>
        </table>
      </div>

      {creating && (
        <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={() => setCreating(false)}>
          <div className="w-full md:w-[600px] bg-white h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b flex items-center justify-between">
              <div className="font-display text-xl font-semibold text-navy">Post a new job</div>
              <button onClick={() => setCreating(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4 text-sm">
              <div>
                <label className="text-xs text-slate-500">Title</label>
                <input required value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                  className="w-full border rounded px-3 py-2 mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500">Industry</label>
                  <select value={form.industry} onChange={e => setForm({...form, industry: e.target.value})} className="w-full border rounded px-3 py-2 mt-1">
                    <option value="">—</option>
                    {industries.map(i => <option key={i.id}>{i.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Employment type</label>
                  <select value={form.employment_type} onChange={e => setForm({...form, employment_type: e.target.value})} className="w-full border rounded px-3 py-2 mt-1">
                    {["full-time","part-time","temporary","contract","seasonal"].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Work arrangement</label>
                  <select value={form.work_arrangement} onChange={e => setForm({...form, work_arrangement: e.target.value})} className="w-full border rounded px-3 py-2 mt-1">
                    {["on-site","hybrid","remote"].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Pay type</label>
                  <select value={form.pay_type} onChange={e => setForm({...form, pay_type: e.target.value})} className="w-full border rounded px-3 py-2 mt-1">
                    <option value="hourly">Hourly</option><option value="salary">Salary</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Salary min</label>
                  <input type="number" value={form.salary_min} onChange={e => setForm({...form, salary_min: e.target.value})} className="w-full border rounded px-3 py-2 mt-1" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Salary max</label>
                  <input type="number" value={form.salary_max} onChange={e => setForm({...form, salary_max: e.target.value})} className="w-full border rounded px-3 py-2 mt-1" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">City</label>
                  <input value={form.city} onChange={e => setForm({...form, city: e.target.value})} className="w-full border rounded px-3 py-2 mt-1" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">State</label>
                  <input value={form.state} onChange={e => setForm({...form, state: e.target.value})} className="w-full border rounded px-3 py-2 mt-1" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Shift</label>
                  <input value={form.shift} onChange={e => setForm({...form, shift: e.target.value})} className="w-full border rounded px-3 py-2 mt-1" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Schedule</label>
                  <input value={form.schedule} onChange={e => setForm({...form, schedule: e.target.value})} className="w-full border rounded px-3 py-2 mt-1" />
                </div>
              </div>

              <div className="border rounded-lg p-3 bg-slate-50">
                <div className="text-xs font-medium text-slate-600 mb-1 inline-flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-gold" /> AI job-description assistant</div>
                <div className="flex gap-2">
                  <input value={aiSkills} onChange={e => setAiSkills(e.target.value)} placeholder="Key skills (comma-separated)" className="flex-1 border rounded px-2 py-1.5 text-sm" />
                  <button type="button" onClick={aiGenerate} disabled={aiBusy} className="btn-navy py-1.5 px-3 text-xs">
                    {aiBusy ? "Drafting…" : "Draft with AI"}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs text-slate-500">Description</label>
                <textarea rows={6} required value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                  className="w-full border rounded px-3 py-2 mt-1" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Required qualifications</label>
                <textarea rows={3} value={form.requirements} onChange={e => setForm({...form, requirements: e.target.value})}
                  className="w-full border rounded px-3 py-2 mt-1" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Preferred qualifications</label>
                <textarea rows={2} value={form.preferred} onChange={e => setForm({...form, preferred: e.target.value})}
                  className="w-full border rounded px-3 py-2 mt-1" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Benefits</label>
                <textarea rows={2} value={form.benefits} onChange={e => setForm({...form, benefits: e.target.value})}
                  className="w-full border rounded px-3 py-2 mt-1" />
              </div>
              <button type="submit" className="btn-navy w-full justify-center">Post job</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
